import { MODAL_AUTH } from '@components/App/Modal/constants'
import Welcome from '@components/App/Welcome'
import { ROUTE_PAGE_HOME } from '@components/Router/constants'
import { AuthActions, oauthLogin } from '@store/reducers/auth'
import { RequestActions } from '@store/requestsSaga/actions'
import { history } from '@store/store'
import * as ActionTypes from '@storeOld/actions'
import { openModal } from '@storeOld/reducers/ui'
import { call, put, take } from 'redux-saga/effects'

import { shouldShowWelcome } from '@/utils'
import { COUPON_STORAGE_KEY, UTM_STORAGE_KEY } from './constants'
import type { OauthEntry } from './types'
import { removeServiceParams, resolveEntryStrategy } from './utils'

const saveUtmLabels = (utmQuery: string) => {
  window.localStorage.setItem(UTM_STORAGE_KEY, utmQuery)
}

const saveCouponCode = (couponCode: string) => {
  window.sessionStorage.setItem(COUPON_STORAGE_KEY, couponCode)
}

// access_token из hash не должен дожить до первого рендера и попасть в историю
const replaceOauthCallbackUrl = (oauth: OauthEntry) => {
  history.replace(oauth.redirectAfterAuth ?? ROUTE_PAGE_HOME)
}

const cleanServiceParams = () => {
  const { pathname, search } = history.location

  const cleanedSearch = removeServiceParams(search)

  if (cleanedSearch !== null) {
    history.replace(`${pathname}${cleanedSearch}`)
  }
}

// ?check_auth (ссылка из инвайт-письма): юзер без аккаунта должен сразу увидеть
// модалку логина/регистрации, а не блокер «Private project». Залогиненному
// модалка не нужна — инвайт бэк уже применил по email, права придут с проектом
function* openAuthModalForGuest() {
  const token = window.localStorage.getItem('token')

  if (token) {
    // fetchAuth уже запущен и ушёл в запрос — ждём итог; протухший токен
    // завершится RESET_AUTH, и это тоже незалогиненный юзер. AUTH_UNAVAILABLE —
    // сеть/бэк недоступны: юзер, возможно, залогинен, форсить логин не по чему
    const action: { type: string } = yield take([
      ActionTypes.SET_AUTH,
      ActionTypes.RESET_AUTH,
      AuthActions.unavailable.type,
    ])

    if (action.type !== ActionTypes.RESET_AUTH) {
      return
    }
  }

  yield put(openModal(MODAL_AUTH))
}

const showWelcome = (skipWelcome: boolean) => {
  const { pathname, search } = history.location
  const token = window.localStorage.getItem('token')

  if (!skipWelcome && shouldShowWelcome(pathname, search, token)) {
    Welcome()
  }
}

// Запускается в main.tsx до первого рендера. Разбирает URL, с которым открыли
// приложение, и до старта делает следующее:
//
// 1. Если пришли с Google-логина (/oauth#access_token=...) — сразу меняем URL
//    на страницу, куда юзер должен попасть после логина (или на главную),
//    и обмениваем access_token на наш токен (oauthLogin → setToken → fetchAuth).
//    Пока идут запросы, приложение закрыто глобальным лодером.
// 2. utm-метки сохраняем в localStorage — их отправит форма регистрации.
// 3. Купон сохраняем в sessionStorage — применится при оплате подписки.
// 4. Служебные параметры (blank, coupon_code, utm) убираем из URL.
// 5. Анониму на чистой главной показываем велком (?blank его пропускает,
//    после Google-логина он не нужен).
// 6. Для обычного входа запрашиваем авторизацию (fetchAuth) отсюда,
//    в oauth-ветке она запускается цепочкой из п.1. Компоненты fetchAuth
//    при старте не вызывают.
// 7. ?check_auth (ссылка из инвайт-письма) — незалогиненному юзеру
//    принудительно открываем модалку логина/регистрации.
//
// Планы (OCT-4736): редирект после обычного логина/регистрации (сейчас — хук
// useRedirectAfterAuth и параметр ?redirectAfterAuth= в URL) тоже станет частью
// этого цикла: куда вести юзера — запомним при открытии модалки логина,
// а поведём — когда авторизация полностью завершится.
export function* appEntry() {
  const strategy = resolveEntryStrategy(history.location)

  if (strategy.oauth !== null) {
    yield call(replaceOauthCallbackUrl, strategy.oauth)
  }

  if (strategy.utmQuery !== null) {
    yield call(saveUtmLabels, strategy.utmQuery)
  }

  if (strategy.couponCode !== null) {
    yield call(saveCouponCode, strategy.couponCode)
  }

  yield call(cleanServiceParams)

  if (strategy.oauth !== null) {
    yield call(oauthLogin, strategy.oauth.accessToken)

    return
  }

  yield call(showWelcome, strategy.skipWelcome)

  yield put(RequestActions.fetchAuth())

  if (strategy.checkAuth) {
    yield call(openAuthModalForGuest)
  }
}
